import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores';
import { useToast } from '@/components/common/Toast';
import { Button } from '@/components/common/Button';
import {
  Loader2, Lock, User, Eye, EyeOff, ShieldCheck,
  Info, KeyRound, ArrowLeft, CheckCircle2, ShieldAlert
} from 'lucide-react';

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(() => {
    if (typeof window !== 'undefined') {
      return Boolean(new URLSearchParams(window.location.search).get('token'));
    }
    return false;
  });

  // Recovery mode state
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryKey, setRecoveryKey] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [confirmAdminPassword, setConfirmAdminPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [recovering, setRecovering] = useState(false);

  const login = useAuthStore((s) => s.login);
  const loginWithSessionToken = useAuthStore((s) => s.loginWithSessionToken);
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Auto-login fallback if token is provided via URL query parameter (?token=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    if (tokenParam && loginWithSessionToken) {
      loginWithSessionToken(tokenParam.trim())
        .then(() => {
          showToast('¡Sesión iniciada correctamente!', 'success');
          navigate('/', { replace: true });
        })
        .catch((err) => {
          showToast(err?.message || 'El enlace de acceso no es válido o ha caducado', 'error');
        })
        .finally(() => setLoading(false));
    }
  }, [loginWithSessionToken, navigate, showToast]);

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    const cleanUser = username.trim();
    const cleanPass = password;

    if (!cleanUser || !cleanPass) {
      showToast('Por favor completa todos los campos', 'error');
      return;
    }

    setLoading(true);
    try {
      await login(cleanUser, cleanPass);
      showToast('¡Sesión iniciada con éxito!', 'success');
      navigate('/', { replace: true });
    } catch (err: any) {
      showToast(err?.message || 'Usuario o contraseña incorrectos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRecoverAdmin = async (e: FormEvent) => {
    e.preventDefault();
    if (!recoveryKey.trim()) {
      showToast('Por favor ingresa el código de rescate maestro', 'error');
      return;
    }

    if (newAdminPassword !== confirmAdminPassword) {
      showToast('La nueva contraseña y su confirmación no coinciden', 'error');
      return;
    }

    if (newAdminPassword.length < 4) {
      showToast('La nueva contraseña debe tener al menos 4 caracteres', 'error');
      return;
    }

    setRecovering(true);
    try {
      const res = await fetch('/api/auth/recover-admin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recoveryKey: recoveryKey.trim(),
          newPassword: newAdminPassword,
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        showToast('¡Contraseña de Administrador restablecida! Ya puedes iniciar sesión', 'success');
        setUsername('admin');
        setPassword(newAdminPassword);
        setShowRecovery(false);
        setRecoveryKey('');
        setNewAdminPassword('');
        setConfirmAdminPassword('');
      } else {
        showToast(data.error || 'Código de rescate inválido', 'error');
      }
    } catch {
      showToast('Error de conexión al recuperar contraseña', 'error');
    } finally {
      setRecovering(false);
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center bg-surface p-4">
      <div className="w-full max-w-md">
        {/* Brand Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brand-primary/10 text-brand-primary mb-3 shadow-xs">
            {showRecovery ? <KeyRound size={24} /> : <Lock size={24} />}
          </div>
          <h1 className="text-2xl font-bold text-text-primary">Fresha Hub</h1>
          <p className="text-xs text-text-secondary mt-1">
            {showRecovery ? 'Recuperación de Acceso de Administrador' : 'Iniciar Sesión en tu Espacio de Trabajo'}
          </p>
        </div>

        {/* Card */}
        <div className="bg-surface border border-border rounded-2xl p-6 shadow-sm">
          {!showRecovery ? (
            /* NORMAL LOGIN FORM */
            <form onSubmit={handleLogin} className="space-y-4">
              {/* Username Input */}
              <div>
                <label htmlFor="username" className="block text-xs font-semibold text-text-primary mb-1.5">
                  Usuario o Identificador
                </label>
                <div className="relative flex items-center">
                  <div className="absolute left-3 text-text-muted pointer-events-none">
                    <User size={16} />
                  </div>
                  <input
                    id="username"
                    type="text"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="ej. admin o recepcion"
                    disabled={loading}
                    required
                    className="w-full pl-9 pr-3 py-2.5 bg-surface-secondary border border-border rounded-xl text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors disabled:opacity-50"
                  />
                </div>
              </div>

              {/* Password Input */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="password" className="block text-xs font-semibold text-text-primary">
                    Contraseña
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowRecovery(true)}
                    className="text-[11px] text-brand-primary hover:underline font-medium cursor-pointer"
                  >
                    ¿Olvidaste tu clave?
                  </button>
                </div>
                <div className="relative flex items-center">
                  <div className="absolute left-3 text-text-muted pointer-events-none">
                    <Lock size={16} />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={loading}
                    required
                    className="w-full pl-9 pr-10 py-2.5 bg-surface-secondary border border-border rounded-xl text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 text-text-muted hover:text-text-primary transition-colors cursor-pointer p-0.5"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading || !username.trim() || !password}
                className="w-full py-2.5 mt-2"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin mr-1.5" />
                    Iniciando sesión...
                  </>
                ) : (
                  'Iniciar Sesión'
                )}
              </Button>
            </form>
          ) : (
            /* EMERGENCY RECOVERY FORM */
            <form onSubmit={handleRecoverAdmin} className="space-y-4 animate-fade-in">
              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-start gap-2.5 text-xs text-amber-900 dark:text-amber-200">
                <ShieldAlert size={16} className="text-amber-500 shrink-0 mt-0.5" />
                <p className="text-[11px] leading-relaxed">
                  Ingresa tu <strong>Código de Rescate Maestro</strong> para redefinir la contraseña del usuario <strong>admin</strong>.
                </p>
              </div>

              {/* Recovery Code */}
              <div>
                <label className="block text-xs font-semibold text-text-primary mb-1.5">
                  Código de Rescate Maestro
                </label>
                <div className="relative flex items-center">
                  <div className="absolute left-3 text-text-muted pointer-events-none">
                    <KeyRound size={16} />
                  </div>
                  <input
                    type="text"
                    value={recoveryKey}
                    onChange={(e) => setRecoveryKey(e.target.value)}
                    placeholder="Ej: FRESHA-RESCUE-ADMIN-2026"
                    disabled={recovering}
                    required
                    className="w-full pl-9 pr-3 py-2 bg-surface-secondary border border-border rounded-xl text-xs font-mono text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                  />
                </div>
              </div>

              {/* New Password */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-text-primary mb-1.5">
                    Nueva Clave
                  </label>
                  <div className="relative flex items-center">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newAdminPassword}
                      onChange={(e) => setNewAdminPassword(e.target.value)}
                      placeholder="Mín. 4 caracteres"
                      disabled={recovering}
                      required
                      className="w-full px-3 py-2 bg-surface-secondary border border-border rounded-xl text-xs text-text-primary"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-2 text-text-muted hover:text-text-primary p-0.5"
                    >
                      {showNewPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-text-primary mb-1.5">
                    Confirmar Clave
                  </label>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={confirmAdminPassword}
                    onChange={(e) => setConfirmAdminPassword(e.target.value)}
                    placeholder="Repite la clave"
                    disabled={recovering}
                    required
                    className="w-full px-3 py-2 bg-surface-secondary border border-border rounded-xl text-xs text-text-primary"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowRecovery(false)}
                  className="flex-1"
                >
                  <ArrowLeft size={14} />
                  <span>Volver</span>
                </Button>

                <Button
                  type="submit"
                  size="sm"
                  disabled={recovering || !recoveryKey.trim() || !newAdminPassword}
                  className="flex-1"
                >
                  {recovering ? (
                    <>
                      <Loader2 size={14} className="animate-spin mr-1" />
                      Restableciendo...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={14} />
                      <span>Restablecer</span>
                    </>
                  )}
                </Button>
              </div>

              <p className="text-[10px] text-text-muted text-center pt-2 border-t border-border">
                ¿Eres personal de atención? Solicita a tu Administrador que restablezca tu acceso desde su panel.
              </p>
            </form>
          )}

          {/* Hint banner */}
          {!showRecovery && (
            <div className="mt-5 p-3.5 bg-brand-primary/5 border border-brand-primary/15 rounded-xl flex items-start gap-2.5 text-xs text-text-secondary">
              <Info size={16} className="text-brand-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-text-primary">Usuario inicial del sistema</p>
                <p className="text-[11px] text-text-secondary mt-0.5">
                  Usuario: <span className="font-mono font-bold text-text-primary">admin</span> &bull; Contraseña: <span className="font-mono font-bold text-text-primary">admin123</span>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Security Badge */}
        <div className="flex items-center justify-center gap-1.5 text-xs text-text-muted mt-5">
          <ShieldCheck size={14} className="text-state-confirmed" />
          <span>Acceso y recuperación de cuenta protegidos en la nube</span>
        </div>
      </div>
    </div>
  );
}
